import { useAuth } from '@/context/AuthContext';
import { ConfigDropdownForm } from '@/pages/config/ConfigDropdownForm';
import { getClientThemes, updateClientTheme, createClientTheme, type ClientTheme } from '@/api/client.service';
import { useState, useEffect } from 'react';
import { Box, Button, Chip, Grid, Typography } from '@mui/material';
import { useNotification } from '@/context/NotificationContext';
import { useLogger } from '@/hooks/useLogger';
import { LogEventType } from '@/api/log.service';
import RenderColorSection from '../theme/RenderColorSection';
import { useBrandTheme } from '@/context/BrandThemeContext';

export const COLOR_KEYS = ['primary_color', 'secondary_color', 'background_color', 'text_color'] as const;

interface ThemeConfigPageProps {
  clientId?: string;
  clientName?: string;
  defaultOpen?: boolean;
  onChangeClient?: () => void;
}

export const ThemeConfigPage = ({ clientId, clientName, defaultOpen, onChangeClient }: ThemeConfigPageProps) => {
  const { notify } = useNotification();
  const { user } = useAuth();
  const { refreshBrandTheme } = useBrandTheme();
  const [loading, setLoading] = useState<boolean>(false);
  const { log } = useLogger();

  const [themeConfig, setThemeConfig] = useState<Partial<ClientTheme>[]>([
    { theme: "light", primary_color: '#1B6B4A', secondary_color: '#F0E9DB', background_color: '#ffffff', text_color: '#3D3A34' },
    { theme: "dark", primary_color: '#5CC08C', secondary_color: '#1C231E', background_color: '#121412', text_color: '#E6E4DE' },
  ]);

  const ColorLabels: Record<string, string> = {
    "primary_color": "primária",
    "secondary_color": "secundária",
    "background_color": "de fundo",
    "text_color": "do texto"
  };

  const handleChange = (mode: string, key: keyof ClientTheme, value: string) => {
    setThemeConfig((prev) =>
      prev.map((t) => (t.theme === mode ? { ...t, [key]: value } : t))
    );
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      await Promise.all(themeConfig.map(async (theme) => {
        const clientID = theme.client_id || clientId || user?.loggedClient?.id

        if (!clientID) {
          throw new Error("ID do cliente não encontrado.");
        }

        if (theme.id) {
          var updatedTheme = await updateClientTheme(theme)

          log(LogEventType.Updated, updatedTheme, "client_theme", updatedTheme.id, "info");
        } else {
          var newTheme = await createClientTheme({
            background_color: theme.background_color || "",
            client_id: clientID,
            primary_color: theme.primary_color || "",
            secondary_color: theme.secondary_color || "",
            text_color: theme.text_color || "",
            theme: theme.theme || "light"
          });

          log(LogEventType.Created, newTheme, "client_theme", newTheme.id, "info");
        }
      }));
      notify("Configurações de tema salvas com sucesso!");

      reloadThemes();
      // Reaplica as cores na hora — mas só quando o tema salvo é o do cliente
      // logado. Um admin editando outro tenant não muda a própria marca.
      const savedClientId = clientId || user?.loggedClient?.id;
      if (savedClientId && savedClientId === user?.loggedClient?.id) {
        refreshBrandTheme();
      }
    } catch (err) {
      notify("Erro ao salvar os temas. Tente novamente mais tarde.", "error");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const reloadThemes = async () => {
    const cid = clientId ?? user?.loggedClient?.id;
    if (!cid) return;

    try {
      const themes = await getClientThemes(cid);
      if (themes && themes.length > 0) {
        setThemeConfig(themes);
      }
    } catch (ex) {
      notify("Nenhum tema existente. Tema padrão carregado", "warning");
      console.error(ex)
    }
  }

  useEffect(() => {
    reloadThemes();
  }, [user, clientId]);

  return (
    <>
      <ConfigDropdownForm
        title='Configuração de Tema'
        subtitle={clientName}
        onSave={handleSave}
        defaultOpen={defaultOpen}
      >
        {loading ? (
          "Carregando temas..."
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {onChangeClient && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Chip size="small" label={`Cliente: ${clientName}`} color="primary" variant="outlined" />
                <Button size="small" onClick={onChangeClient} sx={{ textTransform: 'none', fontSize: '0.75rem' }}>
                  Trocar cliente
                </Button>
              </Box>
            )}
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, color: '#334155' }}>
                Temas
              </Typography>
              <Grid container spacing={4}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <RenderColorSection
                    mode="light"
                    title="Claro"
                    themeConfig={themeConfig}
                    handleChange={handleChange}
                    ColorLabels={ColorLabels}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <RenderColorSection
                    mode="dark"
                    title="Escuro"
                    themeConfig={themeConfig}
                    handleChange={handleChange}
                    ColorLabels={ColorLabels}
                  />
                </Grid>
              </Grid>
            </Box>
          </Box>
        )}
      </ConfigDropdownForm>
    </>
  );
};